import {
  HOUSEHOLD_FACT_FIELDS,
  HOUSEHOLD_FACT_REGISTRY,
  PERSON_FACT_FIELDS,
  PERSON_FACT_REGISTRY,
  type HouseholdFactField,
  type PersonFactField,
} from '@asvise/household-engine/vocabulary'

/**
 * The questions in the groups the framework asks them in.
 *
 * The business app groups its questions into zones: parts of a business that
 * carry an exposure, so the questions inside one are the ones an owner would
 * naturally answer together, and a card can offer four of them rather than a
 * page offering thirteen. This is the same idea and it did not have to be
 * invented, because the framework's section 4.2 already sets its twenty
 * questions out in four lettered groups, and every field in both registries
 * already carries its number.
 *
 * So the membership is read off the registries rather than written out again.
 * A hand-made copy of it is the thing that disagrees with the document: it was
 * exactly the shape of the four sections this screen used to have, which
 * grouped by accounting category ("money coming in and going out") and had no
 * relation to the question set it was implementing.
 *
 * A PHASE SPANS BOTH SUBJECTS, and that is the document's doing rather than a
 * convenience. Group A is entirely about members, group C entirely about the
 * household, and B and D are both: what comes in is a household figure and a
 * share per earner, and cover through work is held by a person while a recent
 * change is the household's. A topic is a topic, and asking it means asking
 * about the household and about each member in it.
 *
 * NOTHING CAN FALL OUT. `UNPLACED` holds any field whose number this file does
 * not recognise, the record prints them in a group of their own, and a test
 * asserts the list is empty. A field quietly belonging to no phase is the
 * failure this codebase keeps meeting: a question in the registry that the
 * engine reads and no screen ever asks.
 */

export const PHASE_CODES = ['A', 'B', 'C', 'D'] as const
export type PhaseCode = (typeof PHASE_CODES)[number]

interface Heading {
  /** What the group is, in words a family would use about itself. */
  title: string
  /**
   * What this part is about. Describes the questions, never what the rules do
   * with the answers: the board says what an answer is for, from the engine's
   * own account, and a hint that promised an outcome here would be a second
   * claim with nothing behind it.
   */
  hint: string
  /** The framework's own heading for the group, so the two can be compared. */
  framework: string
}

/**
 * A `Record`, so a code with no heading fails the build.
 *
 * The same guard as the two field tables in `inputs.tsx`, which is what caught
 * a registry field the form had never asked.
 */
const HEADINGS: Record<PhaseCode, Heading> = {
  A: {
    title: 'Where you live, and who lives with you',
    hint: 'A family of one is still a family. Everyone whose money this is, and everyone who depends on it.',
    framework: 'Localisation and household',
  },
  B: {
    title: 'What comes in, and what is owed',
    hint: 'Monthly figures, after tax, and anything still outstanding.',
    framework: 'Income and financial obligations',
  },
  C: {
    title: 'What you could fall back on',
    hint: 'What you could reach, how long you would want to be able to keep going, and what you could comfortably set aside. These are your choices rather than our assumptions.',
    framework: 'Resilience and liquidity',
  },
  D: {
    title: 'Cover you already have',
    /*
      The promise that nothing here asks about anybody's health belongs to the
      screen rather than to a group of it, and it is already in the lead. Said
      twice it also defeats the check that keeps it true: a scan of the
      questions for the words a health question would use cannot tell the
      promise from a breach of it, and this hint made the questionnaire read as
      though it contained one.
    */
    hint: 'Benefits through work, and anything that has changed recently. Policy documents are read separately.',
    framework: 'Existing protection and work benefits',
  },
}

export interface Phase extends Heading {
  code: PhaseCode
  /** Questions about the household, in the framework's numbered order. */
  household: HouseholdFactField[]
  /** Questions asked of each member, in the framework's numbered order. */
  person: PersonFactField[]
}

/** The letter and the number of a registry entry's question, or null. */
function numbered(question: string): { code: string; at: number } | null {
  const match = /^([A-Z])(\d+)$/.exec(question)
  return match ? { code: match[1] as string, at: Number(match[2]) } : null
}

const isPhase = (code: string): code is PhaseCode =>
  (PHASE_CODES as readonly string[]).includes(code)

/**
 * The fields of one phase, in the order the document numbers them.
 *
 * Declaration order is the tie-break rather than anything alphabetical: five
 * fields share C13 because one framework question asks for five figures, and
 * the registry already has them in the order somebody would say them.
 */
function fieldsOf<F extends string>(
  code: PhaseCode,
  fields: readonly F[],
  questionOf: (field: F) => string,
): F[] {
  return fields
    .map((field, declared) => ({ field, declared, q: numbered(questionOf(field)) }))
    .filter((f) => f.q !== null && f.q.code === code)
    .sort((a, b) => a.q!.at - b.q!.at || a.declared - b.declared)
    .map((f) => f.field)
}

export const PHASES: Phase[] = PHASE_CODES.map((code) => ({
  code,
  ...HEADINGS[code],
  household: fieldsOf(code, HOUSEHOLD_FACT_FIELDS, (f) => HOUSEHOLD_FACT_REGISTRY[f].question),
  person: fieldsOf(code, PERSON_FACT_FIELDS, (f) => PERSON_FACT_REGISTRY[f].question),
}))

/**
 * Fields no phase claims, which should always be none.
 *
 * Exported so the record can print them rather than lose them, and so a test
 * can fail on them. A question whose number this file cannot read is either a
 * new group the framework added or a typo in the registry, and both are worth
 * being told about rather than being shown a form that is quietly one question
 * short.
 */
export const UNPLACED: {
  household: HouseholdFactField[]
  person: PersonFactField[]
} = {
  household: HOUSEHOLD_FACT_FIELDS.filter((f) => {
    const q = numbered(HOUSEHOLD_FACT_REGISTRY[f].question)
    return q === null || !isPhase(q.code)
  }),
  person: PERSON_FACT_FIELDS.filter((f) => {
    const q = numbered(PERSON_FACT_REGISTRY[f].question)
    return q === null || !isPhase(q.code)
  }),
}
